import { useRef, useState, memo } from 'react';
import Box from '@mui/material/Box';
import { Modal as MuiModal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useReactiveVar } from '@apollo/client';

import { JOIN_THE_CLUB_MUTATION } from '@graphql/mutation/joinTheClub';
import { utmQueryVar } from 'store/utils';
import theme from 'src/style-system/theme';
import useClickOutside from '@utils/useClickOutside';
import { validateEmail, validateMobileNumber } from '@utils/validator';
import PageLoader from '@components/PageLoader';

import CopyCodeSection from './CopyCodeSection';
import SendSmsSection from './SendSmsSection';
import EmailSection from './EmailSection ';
import { useStyles } from './useStyles';

interface ModalProps {
  open: boolean;
  handleClose: () => void;
  isMobileNotVisible?: boolean;
  countryCode: string;
}

const ExitIntentModal = ({
  open,
  handleClose,
  isMobileNotVisible = false,
  countryCode,
}: ModalProps) => {
  const [step, setStep] = useState<number>(1);

  const [emailAdd, setEmailAdd] = useState<string>('');
  const [emailErr, setEmailErr] = useState<string>('');
  const [mobileNumber, setMobileNumber] = useState<string>('');
  const [mobileNumberErr, setMobileNumberErr] = useState<string>('');
  const ref = useRef<HTMLDivElement>(null);

  const classes = useStyles();

  useClickOutside(ref, () => {
    handleClose();
  });

  const [joinTheClub, { loading }] = useMutation(JOIN_THE_CLUB_MUTATION);
  const utmQuery = useReactiveVar(utmQueryVar);

  const handleInputEmailChange = (e: any) => {
    if (emailErr) {
      setEmailErr('');
    }
    setEmailAdd(e.target.value);
  };

  const handleInputMobileChange = (e: any) => {
    if (mobileNumberErr) {
      setMobileNumberErr('');
    }
    setMobileNumber(e.target.value);
  };

  const onClickGetDiscount = async () => {
    const isEmailValid = validateEmail(emailAdd);

    const isMobileNumberValid = mobileNumber
      ? validateMobileNumber({ mobileNumber, countryCode })
      : { valid: true };

    if (!isEmailValid.valid) {
      setEmailErr(isEmailValid?.messageKey || '');
      return;
    }

    if (!isMobileNumberValid.valid) {
      setMobileNumberErr(isMobileNumberValid?.messageKey || '');
      return;
    }

    try {
      const response = await joinTheClub({
        variables: {
          input: {
            email: emailAdd,
            element: 'exit intent',
            title: document?.title,
            device: document?.body?.clientWidth > 900 ? 'DT' : 'Mob',
            queryParams: {
              userAgent: window?.navigator?.userAgent,
              UTMQuery: utmQuery,
              device: document?.body?.clientWidth > 900 ? 'DT' : 'Mob',
            },
            phoneNumber: mobileNumber,
          },
        },
      });

      if (response) {
        //@ts-ignore
        window?.dataLayer.push({
          event: 'retention-supression',
          type: 'join-the-club',
        });
        setStep(step + 1);
      }
    } catch (err: any) {
      if (err) {
        mobileNumber
          ? setMobileNumberErr(err?.message || '')
          : setEmailErr(err?.message || '');
      }
    }
  };

  const onClickErrorCloseIcon = () => {
    setEmailErr('');
  };

  const onClickMobileErrorCloseIcon = () => {
    setMobileNumberErr('');
  };

  const getRightSideSlide = (step: number) => {
    switch (step) {
      case 1: {
        return (
          <EmailSection
            emailAdd={emailAdd}
            emailErr={emailErr}
            handleInputEmailChange={handleInputEmailChange}
            onClickErrorCloseIcon={onClickErrorCloseIcon}
            onClickGetDiscount={onClickGetDiscount}
            handleClose={handleClose}
          />
        );
      }
      case 2: {
        return (
          <SendSmsSection
            mobileNumber={mobileNumber}
            mobileNumberErr={mobileNumberErr}
            handleInputMobileChange={handleInputMobileChange}
            onClickErrorCloseIcon={onClickMobileErrorCloseIcon}
            onClickGetDiscount={onClickGetDiscount}
            setStep={setStep}
          />
        );
      }
      case 3: {
        return <CopyCodeSection />;
      }
    }
  };

  return (
    <MuiModal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        [theme.breakpoints.down('md')]: {
          display: isMobileNotVisible ? 'none' : 'block',
        },
      }}
    >
      <Box className={classes.container}>
        <Box className={classes.mainContainer} ref={ref}>
          {loading ? (
            <Box className={classes.pageLoaderWrapper}>
              <PageLoader height="100%" />
            </Box>
          ) : (
            <Box className={classes.mainContainer}>
              <CloseIcon onClick={handleClose} className={classes.closeIcon} />
              <Box className={classes.leftContainer} />
              {getRightSideSlide(step)}
            </Box>
          )}
        </Box>
      </Box>
    </MuiModal>
  );
};

export default memo(ExitIntentModal);
